import React, { useState, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  useToast,
  Select,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { userAPI, User } from '../services/api';

const statusOptions: User['status'][] = ['active', 'inactive', 'onboarding'];

interface FormErrors {
  name?: string;
  email?: string;
  role?: string;
}

const OnboardUser: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<Omit<User, '_id'>>({
    name: '',
    email: '',
    role: '',
    status: 'onboarding',
    joinDate: new Date().toISOString().split('T')[0], // Today's date
  });

  // Refs for focus management
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const roleRef = useRef<HTMLInputElement>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const validateField = (field: keyof FormErrors, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return undefined;
      case 'role':
        if (!value.trim()) return 'Role is required';
        if (value.trim().length < 2) return 'Role must be at least 2 characters';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (field: keyof Omit<User, '_id'>, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleBlur = (field: keyof FormErrors) => {
    const value = formData[field as keyof Omit<User, '_id'>];
    const error = validateField(field, value as string);
    console.log(`Blur validation for ${field}:`, { value, error });
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    newErrors.name = validateField('name', formData.name);
    newErrors.email = validateField('email', formData.email);
    newErrors.role = validateField('role', formData.role);

    console.log('Validation errors:', newErrors);
    setErrors(newErrors);

    // Focus on first invalid field
    if (newErrors.name) {
      nameRef.current?.focus();
      return false;
    }
    if (newErrors.email) {
      emailRef.current?.focus();
      return false;
    }
    if (newErrors.role) {
      roleRef.current?.focus();
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    console.log('Form submission started');
    if (!validateForm()) {
      console.log('Validation failed, returning');
      return;
    }

    try {
      setSaving(true);
      await userAPI.createUser(formData);
      
      toast({
        title: 'User Onboarded Successfully',
        description: 'An email has been sent to the user to set their password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      navigate('/users');
    } catch (error: any) {
      toast({
        title: 'Onboarding Failed',
        description: error.response?.data?.message || 'Failed to onboard user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBackToUsers = () => {
    navigate('/users');
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      role: '',
      status: 'onboarding',
      joinDate: new Date().toISOString().split('T')[0],
    });
    setErrors({});
  };

  return (
    <Box>
      {/* Page Header with Back Navigation */}
      <Box mb={8}>
        <HStack spacing={4} mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={handleBackToUsers}
            colorScheme="primary"
          >
            Back to Users
          </Button>
        </HStack>
        <Heading size="lg" color="gray.800" mb={2}>
          Onboard New User
        </Heading>
        <Text color="gray.600">
          Add a new user to the system and send them setup instructions
        </Text>
      </Box>

      {/* Onboard Form */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          {/* Debug: Current errors state */}
          {/* <Box mb={4} p={2} bg="gray.100" borderRadius="md">
            <Text fontSize="sm">Debug - Errors: {JSON.stringify(errors)}</Text>
          </Box> */}
          
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <FormControl flex={1} minW="300px" isInvalid={!!errors.name}>
                <FormLabel>Name <span className="text-red-500">*</span></FormLabel>
                <Input
                  id="name"
                  ref={nameRef}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  placeholder="Enter user name"
                  borderColor={errors.name ? 'red.500' : undefined}
                  _focus={{ borderColor: errors.name ? 'red.500' : undefined }}
                />
                                 <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>
              
              <FormControl flex={1} minW="300px" isInvalid={!!errors.email}>
                <FormLabel>Email <span className="text-red-500">*</span></FormLabel>
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="Enter email address"
                  borderColor={errors.email ? 'red.500' : undefined}
                  _focus={{ borderColor: errors.email ? 'red.500' : undefined }}
                />
                                 <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack spacing={4} flexWrap="wrap">
              <FormControl flex={1} minW="300px" isInvalid={!!errors.role}>
                <FormLabel>Role <span className="text-red-500">*</span></FormLabel>
                <Input
                  id="role"
                  ref={roleRef}
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  onBlur={() => handleBlur('role')}
                  placeholder="Enter user role (e.g., Manager, Associate, Admin)"
                  borderColor={errors.role ? 'red.500' : undefined}
                  _focus={{ borderColor: errors.role ? 'red.500' : undefined }}
                />
                                 <FormErrorMessage>{errors.role}</FormErrorMessage>
              </FormControl>
              
              <FormControl flex={1} minW="300px">
                <FormLabel>Status</FormLabel>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value as User['status'])}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            <FormControl maxW="300px">
              <FormLabel>Join Date</FormLabel>
              <Input
                id="joinDate"
                type="date"
                value={formData.joinDate}
                onChange={(e) => handleInputChange('joinDate', e.target.value)}
              />
            </FormControl>

            {/* Info Box */}
            <Box
              bg="blue.50"
              border="1px"
              borderColor="blue.200"
              borderRadius="md"
              p={4}
            >
              <Text color="blue.700" fontWeight="medium" mb={2}>
                What happens next?
              </Text>
              <Text color="blue.600" fontSize="sm">
                • A user account will be created with the provided information<br/>
                • An email will be sent to the user with password setup instructions<br/>
                • The user can then login and access the system
              </Text>
            </Box>

            {/* Action Buttons */}
            <HStack spacing={4} pt={4}>
              <Button
                colorScheme="primary"
                type="submit"
                isLoading={saving}
                loadingText="Onboarding..."
                minW="140px"
              >
                Onboard User
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                type="button"
                minW="120px"
              >
                Reset Form
              </Button>
              <Button
                variant="ghost"
                onClick={handleBackToUsers}
                type="button"
                minW="120px"
              >
                Cancel
              </Button>
            </HStack>
          </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default OnboardUser;
